import type { BuyRequest, IRogue } from '@/js/services/RogueTypes'
import { getBuyRequestMeta } from "@/js/helpers/buy-request"

// Use for FM-6 and FM-HR resistance types
export function getFxtEdition(edition: string) {
  if (edition.includes('stack') || edition.includes('250') || edition.includes('300')) {
    return edition.includes('250') ? 'stack 250' : 'stack 300'
  }

  return 'plate loaded footplate'
}

// Used for createFunctionalTrainerConfig
export function determineTrainerVariant(rogue: IRogue, buyRequest: BuyRequest) {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const linkSku = meta.linkSku?.toUpperCase() || ''
  const name = meta.name?.toLowerCase()

  const isAddon = name?.includes('add-on')
  || linkSku.includes('ADD-ON')
  || name?.includes('390ind')
  || name?.includes('trainer') && name?.includes('kit') && !name?.includes('hr90')

  if (isAddon) {
    if (linkSku.includes('SLINGER')) return 'addon-slingerx'
    if (linkSku.includes('RHINO')) return 'addon-rhino'
    if (linkSku.includes('LPLR')) return 'addon-lrlp'
    if (name?.includes('390ind') || linkSku.includes('INDY')) return 'addon-indy'

    const hasTwin = name?.includes('twin')
    const hasHR   = linkSku.includes('HR')

    if (hasTwin && hasHR) return 'addon-twin-hr'
    if (hasTwin) return 'addon-twin'
    if (hasHR) return 'addon-hr'

    return 'addon'
  }

  if (linkSku.includes('SLINGER-X-GROUP')) {
    return 'slingerX'
  }

  if (linkSku.includes('RHINO-TRAINER')) {
    return 'rhino'
  }

  if (linkSku.includes('LPLR')) {
    return 'lat-low-row'
  }

  if (linkSku.includes('INDY')) {
    return 'indy'
  }

  if (linkSku.includes('TWIN') && linkSku.includes('HR')) {
    return 'twin-hr'
  }

  if (linkSku.includes('TWIN')) {
    return 'twin'
  }

  if (linkSku.includes('HR')) {
    return 'hr'
  }

  // Default to FM/FML-6
  return 'standard-6'
}

export function getFxtType(name: string, edition: string) {
  const isLatAndLowRow = name.includes('lat pull low row') || name.includes('wall mount lat pulldown')
  const isIndy = name.includes('indy')

  if (name.includes('twin')) return 'twin'
  if (edition.includes('stack') && !isLatAndLowRow && !isIndy) return edition.includes('250lb') ? 'stack 250' : 'stack 300'
  if (edition.includes('rhino plate')) return 'rhino footplate'
  if (edition.includes('twin')) return 'twin'

  if (isLatAndLowRow) return 'lat'
  if (isIndy) return undefined

  return 'rhino'
}

// If the product is a FXT, format the SKU so we can load the group product
export function formatFxtSKU(productName: string, productSKU: string): string | undefined {
  const name = productName.toLowerCase()
  const sku = productSKU.toLocaleLowerCase()

  const isFXT = name.includes('functional trainer')
  const isRhino = name.includes('rhino trainer')
  const isSlingerX = name.includes('slinger') || sku?.includes('slinger')
  const isSlingerXML = isSlingerX && (name?.includes('monster lite') || sku?.startsWith('ml-'))
  const isFML = name?.includes("fml")
  const isTwin = name?.includes("twin") || name?.includes("hr90") || sku?.includes("twin")
  const isHR = name?.includes("hr")
  const isIndy = name?.includes("indy")
  const prefix = isFML ? "FML" : "FM"

  if (!(isFXT || isRhino || isSlingerX)) return undefined

  const isAddon = name?.includes('add-on')
  || sku?.includes('add-on')
  || name?.includes('390ind')
  || name?.includes('trainer') && name?.includes('kit') && !name?.includes('hr90')

  if (isSlingerXML) return isAddon ? "ML-SLINGER-X-ADD-ON-GROUP" : "ML-SLINGER-X-GROUP"
  if (isSlingerX) return isAddon ? "SLINGER-X-ADD-ON-GROUP" : "SLINGER-X-GROUP"
  if (isIndy) return isAddon ? "INDY-ADD-ON-GROUP" : "INDY-GROUP"
  if (isRhino) return isAddon ? 'FM-Rhino-Group': 'RHINO-TRAINER-GROUP'
  if (isHR && isTwin) return isAddon ? ('FM-HR-TWIN-ADD-ON-GROUP') : (`${prefix}-HR-Twin-Group`) // There is no FML Twin HR Addon
  if (isHR && isFML) return isAddon ? ('FML-HR-Trainer-Group') : ('FML-HR-Group')
  if (isHR && !isFML) return isAddon ? ('FM-HR-ADD-ON-GROUP') : ('FM-HR-Group')
  if (isHR) return isAddon ? (`${prefix}-HR-Trainer-Group`) : (`${prefix}-HR-Group`)
  if (isTwin && !isFML) return isAddon ? (`FM-6-Twin-Trainer-Group`) : (`FM-6-Twin-Group`)
  if (isTwin && isFML)return isAddon ? (`FML-6-Twin-Add-On-Group`) : (`FML-6-Twin-Group`)
  if (productName.includes("6")) return isAddon ? (`${prefix}-6-Trainer-Group`) : (`${prefix}-6-Group`)

  return undefined
}
